import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Play } from "lucide-react";
import { useParallax } from "@/hooks/useParallax";
import { PlatformLogosCarousel } from "./PlatformLogosCarousel";

export function HeroSection() {
  const navigate = useNavigate();
  const parallaxOffset = useParallax(0.3);

  const scrollToHowItWorks = () => {
    const element = document.querySelector("#how-it-works");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-primary overflow-hidden pt-16 md:pt-20">
      {/* Background gradient effects with parallax */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-primary via-primary to-accent/20"
        style={{ transform: `translateY(${parallaxOffset * 0.2}px)` }}
        aria-hidden="true"
      />
      <div 
        className="absolute top-1/4 -left-32 w-64 h-64 bg-accent/30 rounded-full blur-[100px] transition-transform duration-100"
        style={{ transform: `translateY(${parallaxOffset * 0.8}px) translateX(${parallaxOffset * 0.2}px)` }}
        aria-hidden="true"
      />
      <div 
        className="absolute bottom-1/4 -right-32 w-96 h-96 bg-accent-gold/20 rounded-full blur-[120px] transition-transform duration-100"
        style={{ transform: `translateY(${parallaxOffset * 0.5}px) translateX(${-parallaxOffset * 0.15}px)` }}
        aria-hidden="true"
      />
      
      {/* Decorative waveform with parallax */}
      <div 
        className="absolute bottom-0 left-0 right-0 h-32 opacity-10"
        style={{ transform: `translateY(${parallaxOffset * 0.4}px)` }}
        aria-hidden="true"
      >
        <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full h-full" aria-hidden="true">
          <path
            d="M0 50 Q 180 0, 360 50 T 720 50 T 1080 50 T 1440 50"
            stroke="currentColor"
            strokeWidth="2"
            className="text-accent"
          />
          <path
            d="M0 60 Q 180 20, 360 60 T 720 60 T 1080 60 T 1440 60"
            stroke="currentColor"
            strokeWidth="1.5"
            className="text-accent-gold"
          />
        </svg>
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6 py-12 md:py-24">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-secondary/10 border border-secondary/20 mb-6 md:mb-8">
            <span className="w-2 h-2 rounded-full bg-success animate-pulse" />
            <span className="text-sm text-secondary/80">Trusted by 1,000+ Podcasters</span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-secondary mb-2 md:mb-4 leading-tight">
            Your Podcast.{" "}
            <span
              className="bg-clip-text text-transparent [filter:drop-shadow(0_0_25px_rgba(150,82,243,0.5))_drop-shadow(0_0_40px_rgba(150,82,243,0.3))_drop-shadow(0_0_60px_rgba(242,213,147,0.2))]"
              style={{
                backgroundImage: "linear-gradient(to right, hsl(var(--accent)) 0%, hsl(var(--accent-gold)) 100%)",
              }}
            >
              Everywhere.
            </span>
          </h1>

          {/* Platform Logos Carousel */}
          <PlatformLogosCarousel />

          {/* Subheadline */}
          <p className="text-lg sm:text-xl md:text-2xl text-secondary/70 mb-8 md:mb-10 max-w-2xl mx-auto leading-relaxed">
            Distribute, track, and grow your podcast across all major platforms with one click. 
            Let us handle the delivery while you focus on creating.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button
              size="lg"
              className="w-full sm:w-auto bg-accent hover:bg-accent/90 text-accent-foreground font-semibold text-lg px-8 py-6 h-auto transition-transform duration-200 hover:scale-105 active:scale-100"
              onClick={() => navigate("/auth?tab=signup")}
            >
              Get Started Free
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="w-full sm:w-auto bg-neutral/30 border-neutral/50 text-secondary hover:bg-neutral/50 hover:border-neutral/70 font-semibold text-lg px-8 py-6 h-auto transition-transform duration-200 hover:scale-105 active:scale-100"
              onClick={scrollToHowItWorks}
            >
              <Play className="mr-2 h-5 w-5" />
              See How It Works
            </Button>
          </div>

          {/* Social proof mini */}
          <div className="mt-12 md:mt-16 flex flex-wrap items-center justify-center gap-6 md:gap-8 text-secondary/50 text-sm">
            <div className="flex items-center gap-2">
              <div className="flex -space-x-2">
                {[
                  "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=64&h=64&fit=crop&crop=face",
                  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=64&h=64&fit=crop&crop=face",
                  "https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=64&h=64&fit=crop&crop=face",
                  "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=64&h=64&fit=crop&crop=face",
                ].map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt={`Creator ${i + 1}`}
                    width={32}
                    height={32}
                    loading="lazy"
                    decoding="async"
                    className="w-8 h-8 rounded-full border-2 border-primary object-cover"
                  />
                ))}
              </div>
              <span>Join 1k+ creators</span>
            </div>
            <div className="hidden sm:block w-px h-4 bg-secondary/20" />
            <span>No credit card required</span>
            <div className="hidden sm:block w-px h-4 bg-secondary/20" />
            <span>Setup in 2 minutes or less</span>
          </div>
        </div>
      </div>
    </section>
  );
}